import type { Asset } from 'types/asset.types';
import { getFuelUsageDiff, getNotificationIndex } from '../utils';
import {
  fuelIdleConsumptionLabels,
  getFuelLabelWrtUnitSystem,
  getHourlyFuelEfficencyLabelWrtUnitSystem,
} from 'utils/helpers/labels';
import { useAppSelector } from 'store/hook';
import { selectAssets as selectFuelAnalyticsAssets } from 'store/fuelAnalyticsAssetList.slice';
import { useMemo } from 'react';
import AssetItemKPICard from './AssetItemKPICard';
import { truncateTo2dp } from 'utils/helpers/general';
import { convertUtcToDisplayTime } from 'utils/helpers/dateTime';
import {
  notificationLevel,
  assetHasDTC,
  assetHasSuggestions,
  createGroupedOutliers,
  handleTripClickForSuggestions,
  handleAlertClick,
  assetTypeMappings,
} from 'views/FleetOverview/helpers/asset.helpers';
import { MutedTypography } from './AssetCard.styles';
import useAppTheme from 'hooks/useAppTheme';
import { Box, Tooltip } from '@mui/material';
import { SVG } from 'components/Asset/SVG';
import {
  getIsAssetOutOfService,
  svgStyleImport,
  svgStylePerformanceIcon,
} from 'views/FleetOverview/helpers/map.helpers';
import { useNavigate } from 'react-router-dom';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

const _dark = clientThemePalette[CLIENT_THEME].dark;

interface Props {
  asset: Asset;
  dateComparer: string;
}

export function AssetCardItemBody({ asset, dateComparer }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enableDarkTheme } = useAppTheme();
  const unitSystem = useAppSelector(
    (state) => state.persistedReducer.customer.unitSystem
  );

  // Get fuel analytics assets to merge fuel data
  const fuelAnalyticsAssets = useAppSelector(selectFuelAnalyticsAssets);

  // Find the corresponding asset in fuel analytics data and merge fuel usage
  const assetWithFuelData = useMemo(() => {
    const fuelAsset = (fuelAnalyticsAssets as Asset[]).find(
      (fuelAsset: Asset) => fuelAsset.assetId === asset.assetId
    );
    if (fuelAsset?.fuelUsage) {
      return {
        ...asset,
        fuelUsage: fuelAsset.fuelUsage,
      };
    }
    return asset;
  }, [asset, fuelAnalyticsAssets]);
  const assetDiff = useMemo(() => {
    return getFuelUsageDiff(
      assetWithFuelData.fuelUsage,
      assetWithFuelData.utilization,
      assetWithFuelData.productivity,
      dateComparer
    );
  }, [assetWithFuelData, dateComparer]);
  const { latestOperationalData } = useAppSelector(
    (state) => state.deviceReducer
  );
  const { preferredTimeDisplayMode, timezone } = useTimeDisplayMode();

  const assetType = useMemo(() => {
    return asset.assetType;
  }, [asset]);
  const device = useMemo(() => {
    return asset.device.deviceId;
  }, [asset]);

  const hourlyFuelUsageValue = useMemo(() => {
    return assetWithFuelData.fuelUsage?.hourly_fuel_consumption_l;
  }, [assetWithFuelData]);

  const fuelUsageToolTipTitle = useMemo(() => {
    // Helper function to safely get fuel value
    const getFuelValue = (period: string) => {
      const fuelUsage = assetWithFuelData.fuelUsage;
      if (!fuelUsage) return undefined;

      switch (period) {
        case 'day':
          return (
            fuelUsage.hourly_fuel_consumption_l_1day_avg ??
            fuelUsage.fuel_consumption_l
          );
        case '3days':
          return (
            fuelUsage.hourly_fuel_consumption_l_3day_avg ??
            fuelUsage.fuel_consumption_l
          );
        case 'week':
          return (
            fuelUsage.hourly_fuel_consumption_l_7day_avg ??
            fuelUsage.fuel_consumption_l
          );
        default:
          return fuelUsage.fuel_consumption_l;
      }
    };

    // Helper function to create comparison message with conversion
    const createComparisonMessage = (
      period: string,
      value: number | undefined
    ) => {
      if (value) {
        return `${period}: ${value} ${getFuelLabelWrtUnitSystem(
          unitSystem,
          fuelIdleConsumptionLabels.total_fuel_consumed
        )}`;
      }
      return `${period}: NA`;
    };

    const fuelValue =
      getFuelValue(dateComparer) ??
      assetWithFuelData.fuelUsage?.hourly_fuel_consumption_l;

    const periodLabels = {
      day: 'Fuel Rate previous day',
      '3days': 'Fuel Rate previous 3 days',
      week: 'Fuel Rate previous week',
      default: 'Fuel Rate',
    };

    const label =
      periodLabels[dateComparer as keyof typeof periodLabels] ||
      periodLabels.default;
    return createComparisonMessage(label, fuelValue);
  }, [assetWithFuelData, dateComparer, unitSystem]);

  const fuelUsageNavigationState = useMemo(() => {
    return {
      type: assetType,
      value: 0,
      device,
    };
  }, [assetType, device]);

  const productivityValue = useMemo(() => {
    return asset.productivity?.avg_load_per_hour ?? 0;
  }, [asset]);

  const productivityToolTipTitle = useMemo(() => {
    if (assetType !== 'haul_truck') return undefined;

    // Helper function to safely get productivity value
    const getProductivityValue = (period: string) => {
      const productivity = asset.productivity;
      if (!productivity) return undefined;

      switch (period) {
        case 'day':
          return (
            (productivity as Record<string, any>).avg_load_per_hour_1day_avg ??
            productivity.avg_load_per_hour
          );
        case '3days':
          return (
            (productivity as Record<string, any>).avg_load_per_hour_3day_avg ??
            productivity.avg_load_per_hour
          );
        case 'week':
          return (
            (productivity as Record<string, any>).avg_load_per_hour_7day_avg ??
            productivity.avg_load_per_hour
          );
        default:
          return productivity.avg_load_per_hour;
      }
    };

    // Helper function to create comparison message
    const createProductivityMessage = (
      period: string,
      value: number | undefined
    ) => {
      if (value !== undefined && value !== null) {
        return `${period}: ${truncateTo2dp(value)} Tons/hr`;
      }
      return `${period}: NA`;
    };

    const productivityValue = getProductivityValue(dateComparer);
    const periodLabels = {
      day: 'Productive Rate previous day',
      '3days': 'Productive Rate previous 3 days',
      week: 'Productive Rate previous week',
      default: 'Productive Rate',
    };

    const label =
      periodLabels[dateComparer as keyof typeof periodLabels] ||
      periodLabels.default;
    return createProductivityMessage(label, productivityValue);
  }, [asset, assetType, dateComparer]);

  const productivityNavigationState = useMemo(() => {
    return {
      type: assetTypeMappings[assetType],
    };
  }, [assetType]);

  // utilization metrics
  const utilizationValue = useMemo(() => {
    return asset.utilization?.utilization_by_active
      ? asset.utilization.utilization_by_active * 100
      : 0;
  }, [asset]);

  const utilizationToolTipTitle = useMemo(() => {
    // Helper function to safely get utilization value
    const getUtilizationValue = (period: string) => {
      const utilization = asset.utilization;
      if (!utilization) return undefined;

      switch (period) {
        case 'day':
          return (
            (utilization as Record<string, any>)
              .utilization_by_active_1day_avg ??
            utilization.utilization_by_active
          );
        case '3days':
          return (
            (utilization as Record<string, any>)
              .utilization_by_active_3day_avg ??
            utilization.utilization_by_active
          );
        case 'week':
          return (
            (utilization as Record<string, any>)
              .utilization_by_active_7day_avg ??
            utilization.utilization_by_active
          );
        default:
          return utilization.utilization_by_active;
      }
    };

    // Helper function to create comparison message
    const createUtilizationMessage = (
      period: string,
      value: number | undefined
    ) => {
      if (value !== undefined && value !== null) {
        return `${period}: ${truncateTo2dp(value * 100)}%`;
      }
      return `${period}: NA`;
    };

    const utilizationValue = getUtilizationValue(dateComparer);
    const periodLabels = {
      day: 'Utilization previous day',
      '3days': 'Utilization previous 3 days',
      week: 'Utilization previous week',
      default: 'Utilization',
    };

    const label =
      periodLabels[dateComparer as keyof typeof periodLabels] ||
      periodLabels.default;
    return createUtilizationMessage(label, utilizationValue);
  }, [asset, dateComparer]);

  const utilizationNavigationState = useMemo(() => {
    return {
      type: assetType,
      value: 0,
      device,
    };
  }, [assetType, device]);

  // maintenance metrics
  const { notifications } = useAppSelector(
    (state) => state.ruleNotificationsReducer
  );
  const assetNotifications = useMemo(() => {
    return notifications.filter((item: any) => item.assetId === asset.assetId);
  }, [notifications, asset]);
  const activeNotifications = useMemo(() => {
    return assetNotifications.filter((item: any) => item.isActive);
  }, [assetNotifications]);
  const { color, warning } = useMemo(() => {
    return notificationLevel(activeNotifications);
  }, [activeNotifications]);
  const maintenanceToolTipTitle = useMemo(() => {
    if (notifications && notifications.length > 0 && warning !== 'No Alerts') {
      return `Click for drill-down to view Notifications for ${asset.bumperNumber}`;
    }
    return undefined;
  }, [asset]);
  const maintenanceNavigationState = useMemo(() => {
    if (notifications && notifications.length > 0 && warning !== 'No Alerts') {
      const bumperNumber = asset.bumperNumber;
      const dataToPass = {
        bumperNumber,
      };
      return {
        data: dataToPass,
      };
    }
  }, [asset]);
  const maintenanceNavigateTo = useMemo(() => {
    if (notifications && notifications.length > 0 && warning !== 'No Alerts') {
      return '/notifications';
    }
  }, [notifications, warning]);

  const { suggestions } = useAppSelector(
    (state) => state.productionReducer.data.allFleet
  );

  const groupedOutliers = useMemo(() => {
    return createGroupedOutliers(
      suggestions,
      unitSystem,
      preferredTimeDisplayMode,
      timezone
    );
  }, [suggestions, unitSystem, preferredTimeDisplayMode, timezone]);

  const notificationLevelMap = useMemo(() => {
    return getNotificationIndex(assetNotifications);
  }, [assetNotifications]);

  const notificationLevelIndex = useMemo(() => {
    return notificationLevel(assetNotifications);
  }, [assetNotifications]);

  const tooltipStyles = {
    enterDelay: 300,
    slotProps: {
      popper: {
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, 7],
            },
          },
          {
            name: 'preventOverflow',
            options: {
              padding: 8,
              boundariesElement: 'viewport',
            },
          },
        ],
        sx: {
          '& .MuiTooltip-tooltip': {
            backgroundColor: enableDarkTheme
              ? _dark.toolBarBackground
              : '#F3F4F6',
            color: enableDarkTheme ? '#FFFFFF' : '#000000',
            fontSize: '12px',
            maxWidth: '200px',
          },
          '& .MuiTooltip-arrow': {
            color: enableDarkTheme ? _dark.toolBarBackground : '#F3F4F6',
          },
        },
      },
    },
  };

  const handleAlertClickWrapper = (
    asset?: Asset,
    type: 'notifications' | 'dtc' = 'notifications'
  ) => {
    handleAlertClick(navigate, asset, asset, type);
  };

  const handleTripClickWrapper = (asset: Asset, trip: any) => {
    handleTripClickForSuggestions(asset, trip, undefined, navigate);
  };

  return (
    <>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          padding: '10px',
          gap: '10px',
        }}
      >
        {/* fuel used */}
        <AssetItemKPICard
          title={t('fuel_used_hr')}
          value={hourlyFuelUsageValue && truncateTo2dp(hourlyFuelUsageValue)}
          unit={getHourlyFuelEfficencyLabelWrtUnitSystem(
            unitSystem,
            fuelIdleConsumptionLabels.total_fuel_consumed
          )}
          diffValue={assetDiff?.fuelUsage}
          toolTipTitle={fuelUsageToolTipTitle}
          navigateTo="/fuelUsage/overview"
          navigateState={fuelUsageNavigationState}
          trendDirection={assetDiff?.fuelUsage < 0 ? 'up' : 'down'}
        />
        <AssetItemKPICard
          title={t('utilization')}
          value={utilizationValue && truncateTo2dp(utilizationValue)}
          unit="%"
          diffValue={assetDiff?.utilization}
          toolTipTitle={utilizationToolTipTitle}
          navigateTo="/utilization/overview"
          navigateState={utilizationNavigationState}
          trendDirection={assetDiff?.utilization < 0 ? 'down' : 'up'}
        />
        <AssetItemKPICard
          title={t('productivity')}
          value={
            productivityValue !== null && productivityValue !== undefined
              ? truncateTo2dp(productivityValue)
              : 'N/A'
          }
          unit={
            productivityValue !== null && productivityValue !== undefined
              ? 'Tons / hr'
              : ''
          }
          diffValue={assetDiff?.productivity}
          toolTipTitle={productivityToolTipTitle}
          navigateTo="/production/overview"
          trendDirection={assetDiff?.productivity < 0 ? 'down' : 'up'}
          navigateState={productivityNavigationState}
        />
        <AssetItemKPICard
          title={t('maintenance')}
          value={warning}
          unit=""
          valueColor={color}
          toolTipTitle={maintenanceToolTipTitle}
          navigateTo={maintenanceNavigateTo}
          navigateState={maintenanceNavigationState}
        />
        <AssetItemKPICard
          title={t('trips_completed')}
          value={
            latestOperationalData?.[asset.device.deviceId]?.tripCount ?? 'N/A'
          }
          unit="Trips"
        />
        <AssetItemKPICard
          title={t('engine_start_time')}
          value={
            latestOperationalData?.[asset.device.deviceId]?.engineStartTime
              ? convertUtcToDisplayTime(
                  latestOperationalData[asset.device.deviceId].engineStartTime,
                  preferredTimeDisplayMode,
                  timezone,
                  'HH:mm'
                )
              : 'N/A'
          }
          unit=""
        />
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'start',
          alignItems: 'center',
          border: '1px solid #66666666',
          borderRadius: '5px',
          padding: '4px',
          cursor: 'default',
          transition: 'all 0.2s ease-in-out',
          backgroundColor: enableDarkTheme ? _dark.boxBackground : '#F0F3F6',
          margin: '0px 10px 10px 10px',
        }}
      >
        <MutedTypography
          enabledarktheme={enableDarkTheme}
          sx={{ fontWeight: '900' }}
        >
          Notifications
        </MutedTypography>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '4px',
            width: '100%',
            marginLeft: '3%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              width: '80%',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <Tooltip
              title={`Asset is ${
                getIsAssetOutOfService(asset) ? 'Out of Service' : 'Operational'
              }`}
              {...tooltipStyles}
            >
              <Box
                sx={{
                  width: '20px',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <SVG
                  name={'hillsIcon'}
                  style={{
                    height: '1.4em',
                    width: '1.0em',
                    cursor: 'default',
                    ...svgStyleImport(asset),
                  }}
                />
              </Box>
            </Tooltip>
            {notificationLevelMap.message !== 'No Rule-based Alerts' && (
              <Tooltip
                title={
                  notificationLevelMap.message === 'No Rule-based Alerts'
                    ? 'Asset has no Rule-based Alerts'
                    : 'Asset has Rule-based Alerts'
                }
                {...tooltipStyles}
                onClick={() => {
                  handleAlertClickWrapper(asset);
                }}
              >
                <Box
                  sx={{
                    width: '20px',
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  <SVG
                    name={
                      notificationLevelIndex.level <= 1
                        ? 'optimalPerformanceIcon'
                        : 'sensorBasedNotificationsIcon'
                    }
                    style={{
                      fill: '#EB5757',
                      height: '1.4em',
                      width: '1.0em',
                      cursor: 'pointer',
                      ...svgStylePerformanceIcon(notificationLevelMap.color),
                    }}
                  />
                </Box>
              </Tooltip>
            )}
            <Tooltip
              title={`Device is ${asset.device.deviceStatus}`}
              {...tooltipStyles}
            >
              <Box
                sx={{
                  width: '20px',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <SVG
                  name={'fillableXLogo'}
                  fill={
                    asset.device.deviceStatus === 'Online'
                      ? 'rgba(43, 198, 25, 0.8)'
                      : asset.device.deviceStatus === 'Recently Online'
                      ? '#6888d6'
                      : asset.device.deviceStatus === 'Offline'
                      ? '#F47C02'
                      : '#EB5757'
                  }
                  style={{
                    height: '1.4em',
                    width: '1.3em',
                    cursor: 'default',
                  }}
                />
              </Box>
            </Tooltip>

            {assetHasDTC(asset) && (
              <Tooltip
                title={t('asset_has_dtc_alerts')}
                {...tooltipStyles}
                arrow
                placement="bottom"
              >
                <Box
                  sx={{
                    width: '20px',
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                  onClick={() => {
                    handleAlertClickWrapper(asset, 'dtc');
                  }}
                >
                  <SVG
                    name="nonOptimalPerformanceIcon"
                    style={{
                      fill: '#EB5757',
                      height: '1.3em',
                      width: '1.1em',
                      cursor: 'pointer',
                    }}
                  />
                </Box>
              </Tooltip>
            )}

            {assetHasSuggestions(asset, suggestions) > 0 && (
              <Tooltip
                title={`Asset has ${assetHasSuggestions(
                  asset,
                  suggestions
                )}  outlier trips`}
                {...tooltipStyles}
              >
                <Box
                  sx={{
                    width: '20px',
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                  onClick={() => {
                    handleTripClickWrapper(
                      asset,
                      groupedOutliers[asset.bumperNumber][0]
                    );
                  }}
                >
                  <SVG
                    name="oTIcon"
                    style={{
                      fill: '#EB5757',
                      height: '1.4em',
                      width: '1.0em',
                      cursor: 'pointer',
                      ...{ '--icon-fill': '#EB5757' },
                    }}
                  />
                </Box>
              </Tooltip>
            )}
          </Box>
        </div>
      </div>
    </>
  );
}

export default AssetCardItemBody;
